import {
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  BellIcon,
  DocumentDuplicateIcon,
  ExclamationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelRefundRequestById,
  remindStoreByRefundRequestId,
  reportDoubleChargeByTransaction,
  requestRefundByTransaction
} from '../../api/refund-request.api';
import { getTransactionDetail } from '../../api/transaction.api';
import { ReactComponent as BagIcon } from '../../assets/tx_bag.svg';
import { ReactComponent as RefundIcon } from '../../assets/tx_refund.svg';
import { resolvePaymentMethodConfig } from '../../constants/payment-method-logo.const';
import { TransactionDetail } from '../../core/api/http-methods/get-user-transactions';
import { formatCurrency } from '../../utils/fiat-currency.util';
import RefundActionModal from '../home/components/RecentActivities/RefundActionModal';
import { ActivityModalType } from '../home/components/RecentActivities/TransactionListItem';

const extractApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.error?.message ??
      'Something went wrong. Please try again.'
    );
  }
  return 'Something went wrong. Please try again.';
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

type ExpandedActionState = 'available' | 'requested' | 'processed';

const transactionRecordTypeToLabel: Record<string, string> = {
  transaction: 'Transaction',
  order: 'Order',
  refund: 'Refund'
};

const getActionState = (detail: TransactionDetail): ExpandedActionState => {
  const refundRequest = detail.refund_request;
  const isProcessed =
    detail.refund_processed_for_original_transaction ||
    refundRequest?.status === 'processed' ||
    detail.record_type === 'refund';

  if (isProcessed) return 'processed';
  if (refundRequest?.status === 'pending') return 'requested';
  return 'available';
};

const getAlertConfig = (
  detail: TransactionDetail
): { show: boolean; tone: 'orange' | 'green'; message: string } => {
  const refundRequest = detail.refund_request;

  const isProcessed =
    detail.refund_processed_for_original_transaction ||
    refundRequest?.status === 'processed' ||
    detail.record_type === 'refund';

  if (isProcessed) {
    return {
      show: true,
      tone: 'green',
      message:
        'Your refund has been processed. Please allow 3-7 business days for the funds to be reflected in your account.'
    };
  }

  if (refundRequest?.status === 'pending') {
    const requestedDate = new Date(refundRequest.created_at).toLocaleDateString(
      undefined,
      { day: 'numeric', month: 'long', year: 'numeric' }
    );
    const isRefundType = refundRequest.request_type === 'refund';
    return {
      show: true,
      tone: 'orange',
      message: isRefundType
        ? `Refund requested on ${requestedDate}. The store is reviewing your request. If the store approves the refund, a new refund transaction will appear in your history.`
        : `Double charge reported on ${requestedDate}. The store is reviewing your report. If confirmed, a full refund will appear in your history.`
    };
  }

  return { show: false, tone: 'orange', message: '' };
};

const getReminderInfo = (
  detail: TransactionDetail
): { lastReminder: string; nextReminder: string } => {
  const refundRequest = detail.refund_request;
  if (!refundRequest) return { lastReminder: '', nextReminder: '' };

  const nextReminderAt = refundRequest.next_reminder_at
    ? new Date(refundRequest.next_reminder_at).getTime()
    : null;
  const shouldHideNext =
    nextReminderAt !== null && nextReminderAt <= Date.now();

  return {
    lastReminder: refundRequest.last_reminder_at
      ? `Last reminder: ${new Date(refundRequest.last_reminder_at).toLocaleString()}`
      : '',
    nextReminder:
      refundRequest.next_reminder_at && !shouldHideNext
        ? `Next reminder available after ${new Date(refundRequest.next_reminder_at).toLocaleString()}`
        : ''
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <div
    className={clsx(
      'rounded-3xl border border-gray-200 bg-white p-5 shadow-sm',
      className
    )}
  >
    <h2 className="text-md mb-4 mt-2 font-bold uppercase tracking-wider text-gray-700">
      {title}
    </h2>
    {children}
  </div>
);

const BreakdownRow: React.FC<{
  label: string;
  value: string;
  valueClassName?: string;
  bold?: boolean;
}> = ({ label, value, valueClassName, bold }) => (
  <div
    className={clsx(
      'flex items-center justify-between py-2 text-sm',
      bold ? 'font-bold text-gray-900' : 'text-gray-700'
    )}
  >
    <span>{label}</span>
    <span className={valueClassName}>{value}</span>
  </div>
);

const DetailField: React.FC<{
  label: string;
  value: string;
  copyable?: boolean;
}> = ({ label, value, copyable }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    toast.success('Copied!', { duration: 1500 });
  };

  return (
    <div className="py-3">
      <p className="mb-1 text-xs font-medium uppercase tracking-widest text-gray-500">
        {label}
      </p>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900">{value}</p>
        {copyable && (
          <button
            onClick={handleCopy}
            className="ml-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <DocumentDuplicateIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { text: string; className: string }> = {
    success: {
      text: 'Success',
      className: 'text-emerald-600 bg-emerald-100 p-1 rounded'
    },
    confirmed: {
      text: 'Success',
      className: 'text-green-600 bg-green-100 p-1 rounded'
    },
    failed: {
      text: 'Failed',
      className: 'text-red-600 bg-red-100 p-1 rounded'
    },
    pending: {
      text: 'Pending',
      className: 'text-gray-500 bg-gray-100 p-1 rounded'
    }
  };

  const { text, className } = config[status] || {
    text: status,
    className: 'text-gray-500'
  };

  return (
    <span
      className={clsx(
        'mt-1 text-sm font-semibold uppercase tracking-[0.1em]',
        className
      )}
    >
      {text}
    </span>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TransactionDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams<{ transactionId: string }>();

  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ActivityModalType | null>(
    null
  );

  const requestRefundMutation = useMutation({
    mutationFn: requestRefundByTransaction
  });
  const reportDoubleChargeMutation = useMutation({
    mutationFn: reportDoubleChargeByTransaction
  });
  const remindStoreMutation = useMutation({
    mutationFn: remindStoreByRefundRequestId
  });
  const cancelRequestMutation = useMutation({
    mutationFn: cancelRefundRequestById
  });

  useEffect(() => {
    if (!transactionId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);

    getTransactionDetail(transactionId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load transaction details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [transactionId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <p className="text-sm text-gray-500">
          {error ?? 'Transaction not found.'}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-sm font-medium text-orange-600 hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  const actionState = getActionState(detail);
  const alertConfig = getAlertConfig(detail);
  const reminderInfo = getReminderInfo(detail);

  const disableRemindStore =
    Boolean(detail.refund_request?.next_reminder_at) &&
    new Date(String(detail.refund_request?.next_reminder_at)).getTime() >
      Date.now();

  const paymentConfig = resolvePaymentMethodConfig(
    detail.payment_method?.brand
  );

  const formattedDate = dayjs(detail.date).format('MMM DD, YYYY • hh:mm A');

  const currency = detail.currency ?? 'MYR';
  const pb = detail.payment_breakdown;
  const rb = detail.reward_breakdown;

  const handleRequestRefund = () => setActiveModal('requestRefund');
  const handleReportDoubleCharge = () => setActiveModal('reportDoubleCharge');
  const handleCloseModal = () => setActiveModal(null);

  const handleConfirmModalAction = async () => {
    if (!activeModal || !detail.transaction_id) return;
    try {
      if (activeModal === 'requestRefund') {
        await requestRefundMutation.mutateAsync(detail.transaction_id);
        toast.success('Refund requested successfully.');
      } else {
        await reportDoubleChargeMutation.mutateAsync(detail.transaction_id);
        toast.success('Double charge reported successfully.');
      }
      // Refresh detail to reflect new refund_request
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    } finally {
      handleCloseModal();
    }
  };

  const handleRemindStore = async () => {
    const refundRequestId = detail.refund_request?.refund_request_id;
    if (!refundRequestId) return;
    try {
      await remindStoreMutation.mutateAsync(refundRequestId);
      toast.success('Store reminded successfully.');
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    }
  };

  const handleCancelRequest = async () => {
    const refundRequestId = detail.refund_request?.refund_request_id;
    if (!refundRequestId) return;
    try {
      await cancelRequestMutation.mutateAsync(refundRequestId);
      toast.success('Refund request cancelled.');
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    }
  };

  const isModalPending =
    requestRefundMutation.isPending || reportDoubleChargeMutation.isPending;
  const isRemindPending = remindStoreMutation.isPending;
  const isCancelPending = cancelRequestMutation.isPending;

  const showActionButtons =
    actionState !== 'processed' &&
    !detail.is_partially_refunded &&
    detail.record_type === 'transaction' &&
    detail.status === 'success';

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />

      {/* ── Header bar ── */}
      <div className="sticky top-0 z-10 border-b border-gray-100 bg-white">
        <div className="mx-auto flex items-center px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full p-2 transition-colors hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* ── Hero section ── */}
      <div className="mx-auto mt-8 flex max-w-2xl flex-col items-center bg-white px-4 pb-6 pt-4">
        {/* Icon */}
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-300/20">
          {detail.record_type === 'refund' ? (
            <RefundIcon className="h-8 w-auto text-orange-500" />
          ) : detail.record_type === 'order' ? (
            <BagIcon className="h-8 w-auto text-orange-500" />
          ) : (
            <BanknotesIcon className="h-8 w-8 text-orange-500" />
          )}
        </div>

        {/* Labels */}
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">
          {transactionRecordTypeToLabel[detail.record_type] ?? 'Transaction'}
        </p>
        <p className="mt-2 text-3xl font-medium text-orange-500">
          {formatCurrency(pb.total, currency)}
        </p>
        <p className="mt-1 text-sm text-gray-500">{detail.title}</p>
        {/* status pill */}
        <StatusPill status={detail.status} />

        {/* Alert banner */}
        {alertConfig.show && (
          <div
            className={clsx(
              'mt-4 w-full rounded-xl px-4 py-3 text-sm',
              alertConfig.tone === 'orange'
                ? 'bg-orange-50 text-orange-700'
                : 'bg-emerald-50 text-emerald-700'
            )}
          >
            {alertConfig.message}
          </div>
        )}
      </div>

      {/* ── Cards ── */}
      <div className="mx-auto max-w-2xl space-y-3 px-4 py-4 pb-10">
        {/* Payment Breakdown */}
        <SectionCard
          title={
            detail.record_type === 'order'
              ? 'Order Breakdown'
              : 'Payment Breakdown'
          }
        >
          <BreakdownRow
            label="Subtotal"
            value={formatCurrency(pb.subtotal, currency)}
          />
          {pb.discount > 0 && (
            <BreakdownRow
              label="Discount"
              value={`- ${formatCurrency(pb.discount, currency)}`}
              valueClassName="text-gray-700"
            />
          )}
          {pb.service_charge_rate != null &&
            pb.service_charge_amount != null && (
              <BreakdownRow
                label={`Service Charge (${pb.service_charge_rate}%)`}
                value={formatCurrency(pb.service_charge_amount, currency)}
              />
            )}
          {pb.tax_rate != null && pb.tax_amount != null && (
            <BreakdownRow
              label={`Tax (${pb.tax_rate}%)`}
              value={formatCurrency(pb.tax_amount, currency)}
            />
          )}
          <div className="my-2 border-t border-gray-200" />
          <div className="flex items-center justify-between py-2">
            <span className="text-base font-bold text-gray-900">
              Total Amount
            </span>
            <span className="text-xl font-bold text-gray-900">
              {formatCurrency(pb.total, currency)}
            </span>
          </div>
          {pb.refunded_amount != null && pb.refunded_amount > 0 && (
            <BreakdownRow
              label="Refunded"
              value={`- ${formatCurrency(pb.refunded_amount, currency)}`}
              valueClassName="text-red-600"
            />
          )}
        </SectionCard>

        {/* Reward Breakdown */}
        {rb && (
          <SectionCard title="Reward Breakdown">
            {/* Column headers */}
            <div className="mb-3 grid grid-cols-2 gap-4 pt-2">
              <p className="text-xs font-medium uppercase tracking-widest text-gray-500">
                Earned Items
              </p>
              <p className="text-xs font-medium uppercase tracking-widest text-gray-500">
                Redeemed Value
              </p>
            </div>

            {/* Row 1: Points / Point Redemption */}
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <p className="text-xs text-gray-700">Points</p>
                <p className="text-base font-bold text-emerald-700">
                  +{rb.earned.points} pts
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-700">Point Redemption</p>
                <p className="text-base font-bold text-red-700">
                  {rb.redeemed.points_redeemed.toLocaleString()} pts
                </p>
                <p className="text-xs text-gray-700">
                  ({formatCurrency(rb.redeemed.points_redeemed_value, currency)}{' '}
                  value)
                </p>
              </div>
            </div>

            {/* Row 2: Cashback / Applied Discounts */}
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <p className="text-xs text-gray-700">Cashback</p>
                <p className="text-base font-bold text-emerald-700">
                  +{formatCurrency(rb.earned.cashback, currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-700">Applied Discounts</p>
                <p className="text-base font-bold text-red-700">
                  -{formatCurrency(rb.redeemed.applied_discount, currency)}
                </p>
              </div>
            </div>

            {/* Row 3: Stamps / (empty) */}
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <p className="text-xs text-gray-700">Stamps</p>
                <p className="text-base font-bold text-emerald-700">
                  +{rb.earned.stamps} stamp{rb.earned.stamps !== 1 ? 's' : ''}
                </p>
              </div>
              <div />
            </div>
          </SectionCard>
        )}

        {/* Details */}
        <SectionCard title="Details">
          <div className="divide-y divide-gray-100">
            <DetailField label="Order ID" value={detail.order_id} copyable />
            {detail.transaction_id && (
              <DetailField
                label="Transaction ID"
                value={detail.transaction_id}
                copyable
              />
            )}
            <DetailField label="Store" value={detail.store_name} />
            <DetailField label="Date & Time" value={formattedDate} />

            {/* Payment Method */}
            {detail.payment_method && (
              <div className="py-3">
                <p className="mb-1 text-xs font-medium uppercase tracking-widest text-gray-500">
                  Payment Method
                </p>
                <div className="mt-1 flex items-center gap-2">
                  {paymentConfig ? (
                    <div className="flex h-8 w-12 items-center justify-center overflow-hidden rounded border border-gray-200 bg-white px-1">
                      <img
                        src={paymentConfig.logo}
                        alt={paymentConfig.label}
                        className="h-5 object-contain"
                      />
                    </div>
                  ) : null}
                  <p className="text-sm font-medium text-gray-900">
                    {detail.payment_method.method}
                  </p>
                </div>
              </div>
            )}
          </div>
        </SectionCard>

        <div className="pt-6"></div>

        {/* Reminder timestamps */}
        {(reminderInfo.lastReminder || reminderInfo.nextReminder) && (
          <div className="space-y-1 px-1 text-sm italic text-gray-500">
            {reminderInfo.lastReminder && <p>{reminderInfo.lastReminder}</p>}
            {reminderInfo.nextReminder && <p>{reminderInfo.nextReminder}</p>}
          </div>
        )}

        {/* ── Action buttons ── */}
        {showActionButtons && (
          <>
            {actionState === 'available' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleRequestRefund}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-t-1 border-gray-50 bg-white py-4 text-sm font-medium text-orange-600 shadow-lg transition-colors hover:bg-orange-50"
                >
                  <ArrowUturnLeftIcon className="h-5 w-5" />
                  Request Refund
                </button>
                <button
                  type="button"
                  onClick={handleReportDoubleCharge}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-t-1 border-gray-50 bg-white py-4 text-sm font-medium text-orange-600 shadow-lg transition-colors hover:bg-orange-50"
                >
                  <ExclamationCircleIcon className="h-5 w-5" />
                  Report Double Charge
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={disableRemindStore || isRemindPending}
                  onClick={handleRemindStore}
                  className={clsx(
                    'flex w-full items-center justify-center gap-2 rounded-2xl border border-t-1 border-gray-50 bg-white py-4 text-sm font-medium shadow-lg transition-colors',
                    disableRemindStore || isRemindPending
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <BellIcon className="h-5 w-5" />
                  {isRemindPending ? 'Reminding...' : 'Remind Store'}
                </button>
                <button
                  type="button"
                  disabled={isCancelPending}
                  onClick={handleCancelRequest}
                  className={clsx(
                    'flex w-full items-center justify-center gap-2 rounded-2xl border border-t-1 border-gray-50 bg-white py-4 text-sm font-medium shadow-lg transition-colors',
                    isCancelPending
                      ? 'cursor-not-allowed text-orange-500 opacity-60'
                      : 'text-orange-500 hover:bg-orange-50'
                  )}
                >
                  <XMarkIcon className="h-5 w-5" />
                  {isCancelPending ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {activeModal !== null && detail.transaction_id && (
        <RefundActionModal
          activeModal={activeModal}
          amount={String(pb.total)}
          currency={currency}
          isPending={isModalPending}
          onClose={handleCloseModal}
          onConfirm={handleConfirmModalAction}
        />
      )}
    </div>
  );
};

export default TransactionDetailsPage;
